import React, { useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  useMediaQuery,
  Button,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import ReactPlayer from "react-player/lazy";

interface RightPreviewProps {
  file: any;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

const RightPreview: React.FC<RightPreviewProps> = ({
  file,
  onClose,
  onNext,
  onPrevious,
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" && onNext) onNext();
      if (e.key === "ArrowLeft" && onPrevious) onPrevious();
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onNext, onPrevious, onClose]);

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "40%",
        maxWidth: "650px",
        position: "fixed",
        top: 0,
        right: 0,
        height: "100vh",
        zIndex: 1300,
        backdropFilter: "blur(20px)",
        background: "rgba(255,255,255,0.85)",
        borderLeft: "1px solid rgba(15,99,165,0.2)",
        boxShadow: "-10px 0 40px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <motion.div
        initial={{ opacity: 0, x: 120 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 120 }}
        transition={{ duration: 0.4 }}
        style={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "linear-gradient(135deg,#0f63a5,#0b283e)",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {file.name}
          </Typography>

          <IconButton
            onClick={onClose}
            sx={{
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.2)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.35)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Media Section */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "auto",
            background: "#f4f8fc",
          }}
        >
          {file.fileType === "image" && (
            <img
              src={`https://dprstorage.b-cdn.net${file.url}`}
              alt={file.name}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                borderRadius: "16px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              }}
            />
          )}

          {file.fileType === "video" && (
            <ReactPlayer
              url={`https://dprstorage.b-cdn.net${file.url}`}
              controls
              width="100%"
              height="500px"
              style={{ borderRadius: "16px", overflow: "hidden" }}
            />
          )}

          {file.fileType === "document" && (
            <Box
              sx={{
                width: "100%",
                height: "80vh",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              }}
            >
              <iframe
                src={`https://dprstorage.b-cdn.net${file.url}#toolbar=1&navpanes=0`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            </Box>
          )}
        </Box>

        {/* Footer Info */}
        <Divider />
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "#ffffff",
          }}
        >
          <Typography variant="body2" sx={{ color: "#0f63a5" }}>
            <strong>Size:</strong>{" "}
            {file.size > 1024 * 1024
              ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
              : `${(file.size / 1024).toFixed(2)} KB`}
          </Typography>
        </Box>
      </motion.div>
    </Box>
  );
};

export default RightPreview;
